// Roll Positions questionnaire — the PURE, testable state core (no Angular).
//
// Every questionnaire rule is a pure predicate/reducer over RollPositionsInputs so the UI
// invariants can be unit-tested without TestBed (mirrors the Hedge CoPilot's copilot-validation).
// ES5 note: arrays only; no for..of / spread over Set/Map.

import {
    BiasDirection,
    MAX_ROLL_CARDS,
    RollBias,
    RollPositionsInputs,
    Weekday
} from './apg-copilot-domain';

// ---- helpers --------------------------------------------------------------------------

// CR-20260904-001 BR-D1/BR-W3: Mondays are excluded BY DEFAULT in a range — a weekend after-shock
// rule. It is a default, not a wall: the trader may lift any ONE excluded day (toggleWeekdayOverride).
//
// CR-20260915-002: this constant is now only the COMPILED FALLBACK = the APG ORION's default. The
// live starting set is the host's shell-level "Weekdays to exclude" setting, handed in as the
// `defaultWeekdayExclusions` argument below (Monday for the APG ORION, NONE for Maven).
// Always copied (.slice()) into an inputs object — handing out this array itself would let one
// questionnaire's edit mutate the default for every other.
export const DEFAULT_WEEKDAY_EXCLUSIONS: Weekday[] = ['Mon'];

export function emptyRollInputs(defaultWeekdayExclusions?: Weekday[] | null): RollPositionsInputs {
    return {
        date: { kind: 'single', single: null },
        weekdayExclusions: (defaultWeekdayExclusions || DEFAULT_WEEKDAY_EXCLUSIONS).slice(),
        specificDateExclusions: [],
        weekdayOverrides: [],
        bias: { enabled: false, direction: null }
    };
}

export function isValidDate(d: Date | null | undefined): boolean {
    return d instanceof Date && !isNaN(d.getTime());
}

// Weekday of a Date (Mon..Fri), or null for weekends. Indexed by Date.getDay() (0=Sun..6=Sat).
const DOW: (Weekday | null)[] = [null, 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', null];

export function weekdayOf(d: Date): Weekday | null {
    if (!isValidDate(d)) {
        return null;
    }
    return DOW[d.getDay()];
}

// "yyyy-mm-dd" day key (local) — used for set membership + comparison, ignoring time-of-day.
// Lexicographic ordering of these strings matches calendar-date ordering.
export function dayKey(d: Date): string {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return y + '-' + (m < 10 ? '0' + m : '' + m) + '-' + (day < 10 ? '0' + day : '' + day);
}

// Normalized range bounds (start <= end), or null if not a well-formed range. Used for
// rendering/membership; the RUN gate below is stricter (requires start < end on RAW values).
function rangeBounds(i: RollPositionsInputs): { start: Date; end: Date } | null {
    if (i.date.kind !== 'range') {
        return null;
    }
    const s = i.date.start;
    const e = i.date.end;
    if (!isValidDate(s) || !isValidDate(e)) {
        return null;
    }
    return dayKey(s as Date) <= dayKey(e as Date)
        ? { start: s as Date, end: e as Date }
        : { start: e as Date, end: s as Date };
}

export function isInRange(i: RollPositionsInputs, d: Date): boolean {
    const b = rangeBounds(i);
    if (!b || !isValidDate(d)) {
        return false;
    }
    const k = dayKey(d);
    return k >= dayKey(b.start) && k <= dayKey(b.end);
}

// ---- date-kind reducers ---------------------------------------------------------------

export function isSingleDate(i: RollPositionsInputs): boolean {
    return i.date.kind === 'single';
}

// Amendment 1: a single date is a complete choice — exclusions DO NOT apply. Selecting one
// BLOCKS OUT (the UI greys the fieldsets) and CLEARS both exclusion sets.
export function selectSingleDate(i: RollPositionsInputs, d: Date | null): RollPositionsInputs {
    return {
        date: { kind: 'single', single: d },
        weekdayExclusions: [],
        specificDateExclusions: [],
        // The three exclusion sets always empty together: a single date bypasses exclusions
        // entirely (BR-W2), so an override left behind would be a rule with nothing to lift —
        // and it would come back the moment the trader returned to range mode.
        weekdayOverrides: [],
        bias: i.bias
    };
}

export function selectRange(
    i: RollPositionsInputs,
    start: Date | null,
    end: Date | null,
    defaultWeekdayExclusions?: Weekday[] | null
): RollPositionsInputs {
    // ENTERING range mode RE-SEEDS the weekday default (BR-W3; CR-20260915-002: from the host's
    // setting, the compiled Monday only as the fallback). Cause -> effect: selectSingleDate clears
    // the set, so without this a single -> range switch would come back with NO exclusion and the
    // default would silently not hold. On a range -> range edit (a start/end nudge) the set is
    // carried through verbatim, so a deliberate un-toggle survives. Kind change only.
    const enteringRange = i.date.kind !== 'range';
    return {
        date: { kind: 'range', start, end },
        weekdayExclusions: enteringRange
            ? (defaultWeekdayExclusions || DEFAULT_WEEKDAY_EXCLUSIONS).slice()
            : i.weekdayExclusions,
        specificDateExclusions: i.specificDateExclusions,
        weekdayOverrides: i.weekdayOverrides,
        bias: i.bias
    };
}

// ---- weekday exclusions (only meaningful with a range) --------------------------------

export function weekdayExclusionsApplicable(i: RollPositionsInputs): boolean {
    return i.date.kind === 'range';
}

export function toggleWeekdayExclusion(i: RollPositionsInputs, wd: Weekday): RollPositionsInputs {
    if (!weekdayExclusionsApplicable(i)) {
        return i;   // no-op on single date
    }
    const has = i.weekdayExclusions.indexOf(wd) >= 0;
    const next = has
        ? i.weekdayExclusions.filter(w => w !== wd)
        : i.weekdayExclusions.concat([wd]);
    return {
        date: i.date,
        weekdayExclusions: next,
        specificDateExclusions: i.specificDateExclusions,
        weekdayOverrides: i.weekdayOverrides,
        bias: i.bias
    };
}

// ---- weekday overrides (CR-20260904-001 BR-W3/BR-W4, generalised by CR-20260915-002) ------

// The brought-back days as "yyyy-mm-dd" keys, ascending + de-duplicated. WEEKEND / malformed
// entries are IGNORED (defence in depth: toggleWeekdayOverride refuses to record one, and every
// resolver below ignores one anyway). An entry whose weekday is NOT currently excluded stays in
// the list - it is inert while the button is off and lifts again when the button comes back,
// exactly as a Monday override behaved across a Mon un-toggle / re-toggle before this CR.
export function weekdayOverrideKeys(i: RollPositionsInputs): string[] {
    const out: string[] = [];
    const list = i.weekdayOverrides || [];
    list.forEach(d => {
        if (isValidDate(d) && weekdayOf(d) !== null) {
            const k = dayKey(d);
            if (out.indexOf(k) < 0) {
                out.push(k);
            }
        }
    });
    out.sort();
    return out;
}

// True when the day's weekday IS excluded and the trader brought this day back.
export function isWeekdayOverridden(i: RollPositionsInputs, d: Date): boolean {
    const wd = weekdayOf(d);
    if (!wd || i.weekdayExclusions.indexOf(wd) < 0) {
        return false;
    }
    return weekdayOverrideKeys(i).indexOf(dayKey(d)) >= 0;
}

// Un-exclude (or re-exclude) ONE day the weekday rule excludes. The weekday default is a default,
// not a wall — this is the only escape hatch, and it lifts the WEEKDAY rule only, never a
// deliberate specific-date carve-out. Guard shape mirrors toggleSpecificDateExclusion:
//   - a weekend / malformed day        -> no-op
//   - not a well-formed in-range day   -> no-op
//   - not a listed expiration          -> no-op (empty list = chain not loaded -> allow, as elsewhere)
//   - its weekday not currently excluded and not already overridden -> no-op (nothing to lift)
export function toggleWeekdayOverride(i: RollPositionsInputs, d: Date, availableDates?: string[]): RollPositionsInputs {
    const wd = weekdayOf(d);
    if (!wd) {
        return i;
    }
    if (i.date.kind !== 'range' || !isInRange(i, d)) {
        return i;
    }
    const avail = availableDates || [];
    const k = dayKey(d);
    if (avail.length > 0 && avail.indexOf(k) < 0) {
        return i;
    }
    const already = weekdayOverrideKeys(i).indexOf(k) >= 0;
    if (!already && i.weekdayExclusions.indexOf(wd) < 0) {
        return i;
    }
    const list = i.weekdayOverrides || [];
    const next = already
        ? list.filter(x => !(isValidDate(x) && dayKey(x) === k))
        : list.concat([d]);
    return {
        date: i.date,
        weekdayExclusions: i.weekdayExclusions,
        specificDateExclusions: i.specificDateExclusions,
        weekdayOverrides: next,
        bias: i.bias
    };
}

// ---- specific-date exclusions + the calendar render/interaction contract --------------

export type CalendarDayState =
    'unavailable' | 'in-range' | 'excluded-weekday' | 'excluded-specific' | 'override-weekday';

// Amendment 2: the exclusion calendar shows out-of-range days as UNAVAILABLE, weekday-excluded
// in-range days as EXCLUDED + LOCKED (can't be toggled off — complement invariant), and the
// remaining in-range days as toggleable.
export function calendarDayState(i: RollPositionsInputs, day: Date, availableDates?: string[]): CalendarDayState {
    if (i.date.kind !== 'range' || !isInRange(i, day)) {
        return 'unavailable';
    }
    // Layer 2: the roll-date candidate universe IS the option chain's listed expirations. A day that
    // is in range but NOT a listed expiration (weekend, holiday, non-expiration weekday) is not
    // rollable -> unavailable. An omitted OR EMPTY availableDates means "chain not loaded yet" -> every
    // in-range day stays a candidate (so the calendar never renders all-blank during the async load;
    // the calc's resolveSurvivingExpirations still yields [] for a genuinely empty chain).
    if (availableDates && availableDates.length > 0 && availableDates.indexOf(dayKey(day)) < 0) {
        return 'unavailable';
    }
    const wd = weekdayOf(day);
    const k = dayKey(day);
    const isSpecific = i.specificDateExclusions.some(x => dayKey(x) === k);
    if (wd && i.weekdayExclusions.indexOf(wd) >= 0) {
        // CR-20260904-001 BR-W4: a day the trader brought back reads as lifted — unless he also
        // carved it out as a specific date, which still excludes it. The override lifts the WEEKDAY
        // rule only, never a deliberate carve-out.
        if (isWeekdayOverridden(i, day)) {
            return isSpecific ? 'excluded-specific' : 'override-weekday';
        }
        return 'excluded-weekday';
    }
    return isSpecific ? 'excluded-specific' : 'in-range';
}

// A specific-date exclusion only ever ADDS to (or removes its own) carve-out. It is a NO-OP
// on days that are unavailable (out of range) or weekday-excluded (can't contradict a weekday
// exclusion) — this is the complement invariant made mechanical.
export function toggleSpecificDateExclusion(i: RollPositionsInputs, d: Date, availableDates?: string[]): RollPositionsInputs {
    const st = calendarDayState(i, d, availableDates);
    if (st !== 'in-range' && st !== 'excluded-specific') {
        return i;   // unavailable / excluded-weekday -> locked
    }
    const k = dayKey(d);
    const has = i.specificDateExclusions.some(x => dayKey(x) === k);
    const next = has
        ? i.specificDateExclusions.filter(x => dayKey(x) !== k)
        : i.specificDateExclusions.concat([d]);
    return {
        date: i.date,
        weekdayExclusions: i.weekdayExclusions,
        specificDateExclusions: next,
        weekdayOverrides: i.weekdayOverrides,
        bias: i.bias
    };
}

// ---- directional bias -----------------------------------------------------------------

// Yes reveals the direction group; No hides it and CLEARS the direction.
export function setBiasEnabled(i: RollPositionsInputs, on: boolean): RollPositionsInputs {
    const bias: RollBias = on
        ? { enabled: true, direction: i.bias.direction }
        : { enabled: false, direction: null };
    return {
        date: i.date,
        weekdayExclusions: i.weekdayExclusions,
        specificDateExclusions: i.specificDateExclusions,
        weekdayOverrides: i.weekdayOverrides,
        bias
    };
}

export function setBiasDirection(i: RollPositionsInputs, dir: BiasDirection): RollPositionsInputs {
    return {
        date: i.date,
        weekdayExclusions: i.weekdayExclusions,
        specificDateExclusions: i.specificDateExclusions,
        weekdayOverrides: i.weekdayOverrides,
        bias: { enabled: true, direction: dir }
    };
}

// ---- exclusion union (complement invariant) + surviving dates -------------------------

// Exclusions only ever ADD: a day is excluded if its weekday is excluded OR it is a specific
// exclusion. There is no "include" operation, so the two kinds can never contradict.
export function isExcluded(i: RollPositionsInputs, d: Date): boolean {
    const wd = weekdayOf(d);
    if (wd && i.weekdayExclusions.indexOf(wd) >= 0) {
        // CR-20260904-001 BR-W4: a brought-back day escapes the WEEKDAY rule. It can still be
        // excluded by a specific-date carve-out, which the check below applies.
        if (!isWeekdayOverridden(i, d)) {
            return true;
        }
    }
    const k = dayKey(d);
    return i.specificDateExclusions.some(x => dayKey(x) === k);
}

// The dates a Run actually targets: single => [that day]; range => every day start..end that
// survived the exclusions (ascending). Never re-includes an excluded day.
export function survivingDates(i: RollPositionsInputs): Date[] {
    if (i.date.kind === 'single') {
        return isValidDate(i.date.single) ? [i.date.single as Date] : [];
    }
    const b = rangeBounds(i);
    if (!b) {
        return [];
    }
    const out: Date[] = [];
    const cur = new Date(b.start.getFullYear(), b.start.getMonth(), b.start.getDate());
    const endKey = dayKey(b.end);
    while (dayKey(cur) <= endKey) {
        if (!isExcluded(i, cur)) {
            out.push(new Date(cur.getFullYear(), cur.getMonth(), cur.getDate()));
        }
        cur.setDate(cur.getDate() + 1);
    }
    return out;
}

// ---- RUN gating -----------------------------------------------------------------------

// ---- range cap (CR-20260904-001, BR-W1 / REQ-4; follow-on 2026-09-08) -----------------

// The cap is measured from the trader's chosen START date — never from today. Its WIDTH is the
// shell-level "Max Query Range (days)" ORION setting (default 21), and since the 2026-09-08
// ruling it binds EVERYONE — the super user included. The message quotes the configured number,
// so the owner's sentence (O25) keeps its shape whatever the setting says.
export function rangeCapMessage(maxRangeDays: number): string {
    return 'You Cannot Select a Range Wider Than ' + maxRangeDays + ' Days From Your Start Date.';
}

// maxRangeDays null = UNLIMITED (only the state before the setting is known).
export interface RollDateLimits {
    maxRangeDays: number | null;
}

// Whole calendar days from start to end, or null unless a well-formed range.
// UTC-normalised on purpose: a clock change inside the range makes a LOCAL midnight-to-midnight
// subtraction 20.958 days, and then rounding either hides a real overrun or refuses a legal
// 21-day range. Both bounds become UTC midnights, so the difference is an exact day count.
export function rangeSpanDays(i: RollPositionsInputs): number | null {
    const b = rangeBounds(i);
    if (!b) {
        return null;
    }
    const s = Date.UTC(b.start.getFullYear(), b.start.getMonth(), b.start.getDate());
    const e = Date.UTC(b.end.getFullYear(), b.end.getMonth(), b.end.getDate());
    return Math.round((e - s) / 86400000);
}

// True only when a limit APPLIES and the range is wider than it. No limits object, or a null
// maxRangeDays, means unlimited. A single date is never capped (rangeSpanDays is null).
export function exceedsRangeCap(i: RollPositionsInputs, limits?: RollDateLimits): boolean {
    if (!limits || limits.maxRangeDays == null) {
        return false;
    }
    const span = rangeSpanDays(i);
    return span != null && span > limits.maxRangeDays;
}

// RUN is enabled when the date is valid (single: a real Date; range: start strictly before
// end) AND, if a bias is enabled, a direction is chosen. Exclusions NEVER gate (an all-excluded
// range is allowed — it surfaces as the empty-cards state, not a disabled Run).
// CR-20260904-001: `limits` is OPTIONAL, so every existing call site keeps today's behaviour; a
// caller that passes a client limit additionally refuses a range wider than the cap (BR-W1).
export function isRollComplete(i: RollPositionsInputs, limits?: RollDateLimits): boolean {
    let dateValid: boolean;
    if (i.date.kind === 'single') {
        dateValid = isValidDate(i.date.single);
    } else {
        const s = i.date.start;
        const e = i.date.end;
        dateValid = isValidDate(s) && isValidDate(e) && dayKey(s as Date) < dayKey(e as Date);
    }
    if (!dateValid) {
        return false;
    }
    if (i.bias.enabled && i.bias.direction == null) {
        return false;
    }
    if (exceedsRangeCap(i, limits)) {
        return false;
    }
    return true;
}

// Cap the ranked short-list to the "Show top N results" ORION setting. A missing or malformed N
// falls back to the default (5). A non-positive N yields no cards: the owner ruled "no floor, no
// ceiling - whatever they set", so the setting is honoured rather than corrected here.
//
// ⚠ ITS ROLE CHANGED ON 2026-09-10 (CR-20260910-001, ruling R-B) - READ BEFORE MOVING IT. It used
// to be the LAST STEP OF THE RUN: the browser skill ordered its cards and cut the list before
// handing it over, so a date beyond N was calculated and thrown away. Now the run delivers every
// window date it priced, and this is a DISPLAY LIMIT applied by the results list at render, after
// the trader's own sort. Cause and effect: the Price Box feeds its columns from that same delivery,
// so a cut made any earlier would blank the columns of every date it dropped. There is no top-N on
// the wire and no top-N on the backend.
export function capCards<T>(list: T[], maxCards?: number | null): T[] {
    const n = (typeof maxCards === 'number' && isFinite(maxCards)) ? Math.trunc(maxCards) : MAX_ROLL_CARDS;
    return list.slice(0, Math.max(0, n));
}

// ---- chain-scoped expiration resolution (the calc's customDates) ----------------------

// Parse a "yyyy-mm-dd" day key back to a local Date (midnight). Null if malformed.
export function parseDayKey(k: string): Date | null {
    if (!k) {
        return null;
    }
    const parts = k.split('-');
    if (parts.length !== 3) {
        return null;
    }
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) {
        return null;
    }
    return new Date(y, m - 1, d);
}

// Weekday (Mon..Fri) of a "yyyy-mm-dd" day key, or null (weekend / malformed).
//
// ⚠ UNCALLED SINCE 2026-09-10, AND KEPT DELIBERATELY (CR-20260910-001 milestone 1). Its two callers
// were `isDisqualifiedMonday` and `resolveSurvivingExpirations`, both of which migrated to the
// backend. It stays because the split table the leader approved marked it (with `parseDayKey`, its
// only caller now) as staying, and because a key-to-weekday reading is the questionnaire's own kind
// of question - the exclusion calendar reasons in day keys. Named here rather than left to be
// discovered: if the leader would rather it went, it goes in one edit and takes `parseDayKey` with
// it, since nothing else calls that either.
export function weekdayOfKey(k: string): Weekday | null {
    const d = parseDayKey(k);
    return d ? weekdayOf(d) : null;
}

// ⚠ WHAT LEFT THIS FILE ON 2026-09-10 (CR-20260910-001 milestone 1, contract A1 §4.1-4.2):
// `resolveSurvivingExpirations` (contract C3 — "what the trader kept"), `resolveWindowExpirations`
// (C2b — the window) and `resolveUserExcludedDates` (C2 — the window MINUS the survivors). All
// three went together, deliberately: C2 was DEFINED as the complement of C3, so removing one
// without the other would have deleted the definition of "kept" and taken C2 with it silently.
//
// THE BACKEND RESOLVES THE WINDOW NOW, and it has to. Maven builds the same pricing-settings array
// on the backend from a template id (contract M1 §3) and orders the roll in process (A1 §6) — it
// has no browser to ask. So the window rule lives in exactly one place, and the order form carries
// what that rule needs: the date kind, the dates, and the three exclusion lists exactly as the
// trader set them. `customDates` travels EMPTY on the settings; the backend fills it from the
// window it resolved.
//
// ⚠ Do not re-add a resolver here "just for customDates". That is how one rule becomes two.

